import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageTransition } from "@/components/PageTransition";
import { SoundProvider } from "@/contexts/SoundContext";
import { SoundToggle } from "@/components/SoundToggle";
const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Gallery = lazy(() => import("./pages/Gallery"));
const SocialContent = lazy(() => import("./pages/SocialContent"));
const Residential = lazy(() => import("./pages/Residential"));
const Commercial = lazy(() => import("./pages/Commercial"));
const Specialty = lazy(() => import("./pages/Specialty"));
const Emergency = lazy(() => import("./pages/Emergency"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Blog = lazy(() => import("./pages/Blog"));
const WhatToExpect = lazy(() => import("./pages/WhatToExpect"));
const Pricing = lazy(() => import("./pages/Pricing"));
const LicensesCertifications = lazy(() => import("./pages/LicensesCertifications"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const CityServiceArea = lazy(() => import("./pages/CityServiceArea"));
const ZipServiceArea = lazy(() => import("./pages/ZipServiceArea"));
const InsuranceClaims = lazy(() => import("./pages/InsuranceClaims"));
const ServiciosEnEspanol = lazy(() => import("./pages/ServiciosEnEspanol"));
const MarketingContent = lazy(() => import("./pages/MarketingContent"));
const BusinessCard = lazy(() => import("./pages/BusinessCard"));
const EmailSignature = lazy(() => import("./pages/EmailSignature"));
const ContactCard = lazy(() => import("./pages/ContactCard"));

// Medical Environmental Health Division Pages
const MedicalLanding = lazy(() => import("./pages/medical/MedicalLanding"));
const InfectionControl = lazy(() => import("./pages/medical/InfectionControl"));
const HCBSServices = lazy(() => import("./pages/medical/HCBSServices"));
const BehavioralHealth = lazy(() => import("./pages/medical/BehavioralHealth"));
const MedicalNecessity = lazy(() => import("./pages/medical/MedicalNecessity"));
const WhoWeServe = lazy(() => import("./pages/medical/WhoWeServe"));
const Certifications = lazy(() => import("./pages/medical/Certifications"));
const Partnerships = lazy(() => import("./pages/medical/Partnerships"));
const MedicalIntake = lazy(() => import("./pages/medical/MedicalIntake"));
const NJEnrollment = lazy(() => import("./pages/medical/NJEnrollment"));
const NYEnrollment = lazy(() => import("./pages/medical/NYEnrollment"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <SoundProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <PageTransition>
              <Suspense fallback={<div className="min-h-screen bg-background" />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/social" element={<SocialContent />} />
                  <Route path="/residential" element={<Residential />} />
                  <Route path="/commercial" element={<Commercial />} />
                  <Route path="/specialty" element={<Specialty />} />
                  <Route path="/emergency" element={<Emergency />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/what-to-expect" element={<WhatToExpect />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/certifications" element={<LicensesCertifications />} />
                  
                  {/* Service Areas, ZIP Codes & Insurance */}
                  <Route path="/service-areas" element={<ServiceAreas />} />
                  <Route path="/service-areas/:citySlug" element={<CityServiceArea />} />
                  <Route path="/zip/:zipCode" element={<ZipServiceArea />} />
                  <Route path="/insurance-claims" element={<InsuranceClaims />} />
                  <Route path="/servicios-en-espanol" element={<ServiciosEnEspanol />} />
                  <Route path="/marketing-content" element={<MarketingContent />} />
                  <Route path="/card" element={<BusinessCard />} />
                  <Route path="/email-signature" element={<EmailSignature />} />
                  <Route path="/c" element={<ContactCard />} />
                  
                  {/* Medical Environmental Health Division Routes */}
                  <Route path="/medical" element={<MedicalLanding />} />
                  <Route path="/medical/infection-control" element={<InfectionControl />} />
                  <Route path="/medical/hcbs" element={<HCBSServices />} />
                  <Route path="/medical/behavioral-health" element={<BehavioralHealth />} />
                  <Route path="/medical/compliance" element={<MedicalNecessity />} />
                  <Route path="/medical/who-we-serve" element={<WhoWeServe />} />
                  <Route path="/medical/certifications" element={<Certifications />} />
                  <Route path="/medical/partnerships" element={<Partnerships />} />
                  <Route path="/medical/intake" element={<MedicalIntake />} />
                  <Route path="/medical/nj-enrollment" element={<NJEnrollment />} />
                  <Route path="/medical/ny-enrollment" element={<NYEnrollment />} />
                  
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </PageTransition>
            <SoundToggle />
          </BrowserRouter>
        </TooltipProvider>
      </SoundProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
